import { Link } from "react-router-dom";
import { Mic, MessageSquare, Users, Sparkles, ArrowUpRight, Film } from "lucide-react";
import { HOST_PHOTO_1, HOST_PHOTO_2, SOCIALS } from "@/data/site";
import StudioBackdrop from "@/components/podcast/StudioBackdrop";
import { StudioMic, Headphones } from "@/components/podcast/StudioGear";

const PILLARS = [
  {
    icon: MessageSquare,
    title: "Both Sides Of The Table",
    body:
      "Unlike platforms that pick a lane, every conversation invites men and women to the same mic — challenging assumptions instead of confirming them.",
    color: "#E10600",
  },
  {
    icon: Users,
    title: "Diverse Voices",
    body:
      "Guests come from every background — coaches, creators, fathers, mothers, single, married, divorced — each bringing a perspective the algorithm tends to bury.",
    color: "#1E6FE0",
  },
  {
    icon: Sparkles,
    title: "Curiosity, Not Conclusions",
    body:
      "The goal isn't to tell you what to think. It's to surface the questions worth sitting with and let the audience make up their own mind.",
    color: "#E10600",
  },
  {
    icon: Mic,
    title: "Prove Your Point",
    body:
      "Strong opinions are welcome. Empty takes aren't. Every guest is held to one standard: if you have a point, be prepared to prove it.",
    color: "#1E6FE0",
  },
];

const TOPICS_LONG = [
  "Modern Dating",
  "Relationships",
  "Gender Dynamics",
  "Accountability",
  "Personal Growth",
  "Marriage",
  "Fatherhood",
  "Hookup Culture",
  "Social Trends",
  "Cultural Commentary",
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32 pb-20 min-h-screen bg-[#0A0A0A] relative film-grain overflow-hidden">
      <StudioBackdrop opacity={0.14} size={260} />

      {/* Decorative studio gear */}
      <StudioMic
        className="absolute -left-24 top-32 w-96 hidden lg:block float-slow"
        color="#1E6FE0"
        strokeWidth={1}
        style={{ opacity: 0.07 }}
      />
      <Headphones
        className="absolute -right-28 top-2/3 w-[32rem] hidden lg:block float-medium"
        color="#E10600"
        strokeWidth={1}
        style={{ opacity: 0.07 }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="mb-16 max-w-4xl">
          <p className="text-xs tracking-[0.25em] uppercase font-bold mb-4 flex items-center gap-3">
            <span className="symbol-male text-base">♂</span>
            <span className="text-[#1E6FE0]">His Side.</span>
            <span className="text-zinc-600">·</span>
            <span className="text-[#E10600]">Her Side.</span>
            <span className="symbol-female text-base">♀</span>
            <span className="text-zinc-500">The Conversation.</span>
          </p>
          <h1 className="font-display text-6xl md:text-7xl lg:text-8xl uppercase tracking-tight leading-[0.9]">
            About <span className="text-[#1E6FE0]">The</span> <span className="text-[#E10600]">Show</span>
          </h1>
          <p className="mt-6 text-lg text-zinc-300 max-w-3xl">
            A relationship, culture, and social commentary podcast hosted by{" "}
            <span className="text-white font-bold">Manuel Santana</span>.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={SOCIALS.imdb}
              target="_blank"
              rel="noreferrer"
              data-testid="about-show-imdb-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#F5C518] text-black font-bold tracking-[0.15em] uppercase text-xs hover:bg-[#FFD43B] transition-colors"
            >
              <Film className="w-4 h-4" />
              View On IMDb
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <Link to="/episodes" data-testid="about-show-episodes-btn" className="btn-secondary">
              Watch Episodes
            </Link>
          </div>
        </header>

        {/* Big copy block */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-24 items-start border-t border-[#262626] pt-16">
          <div className="lg:col-span-7 space-y-6 text-zinc-300 text-base md:text-lg leading-relaxed">
            <p>
              The <span className="text-white font-bold">Prove Your Point Podcast</span>{" "}
              is a relationship, culture, and social commentary podcast hosted by{" "}
              <span className="text-white font-bold">Manuel Santana</span>.
            </p>
            <p>
              Created to bridge the growing divide between men and women, the show
              explores the conversations that many people are thinking about but few are
              willing to have openly. Through candid discussions, debates, interviews,
              and audience-driven conversations, the podcast examines{" "}
              <span className="text-white">modern dating</span>,{" "}
              <span className="text-white">relationships</span>,{" "}
              <span className="text-white">gender dynamics</span>,{" "}
              <span className="text-white">accountability</span>,{" "}
              <span className="text-white">personal growth</span>, and{" "}
              <span className="text-white">contemporary culture</span>.
            </p>
            <p>
              Unlike many platforms that focus exclusively on one perspective, The Prove
              Your Point Podcast encourages dialogue from both sides of the conversation.
              The goal is not to tell people what to think, but to{" "}
              <span className="text-[#E10600] font-bold">challenge assumptions</span>,{" "}
              <span className="text-[#1E6FE0] font-bold">explore different viewpoints</span>
              , and create meaningful discussions that help people better understand
              themselves and each other.
            </p>
            <p>
              Hosted by media personality and commentator{" "}
              <span className="text-white font-bold">Manuel Santana</span>, the podcast
              features guests from diverse backgrounds who bring unique experiences,
              opinions, and perspectives to the table. Whether discussing dating,
              relationships, social trends, personal development, or controversial topics
              shaping modern culture — every episode is built around one idea:
            </p>

            <blockquote
              data-testid="about-pull-quote"
              className="relative border-l-4 border-[#E10600] pl-6 py-2 my-8"
            >
              <p className="font-display text-3xl md:text-5xl uppercase leading-[0.95] text-white">
                If you have a point,<br />
                <span className="text-[#E10600]">be</span>{" "}
                <span className="text-[#1E6FE0]">prepared</span> to prove it.
              </p>
            </blockquote>
          </div>

          {/* Side photo card */}
          <aside className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -bottom-3 -right-3 w-full h-full border-2 border-[#E10600] hidden md:block" />
              <div className="absolute -top-3 -left-3 w-full h-full border-2 border-[#1E6FE0] hidden md:block" />
              <div className="relative bg-[#0A0A0A] border border-[#262626] overflow-hidden">
                <img
                  src={HOST_PHOTO_2}
                  alt="Manuel Santana"
                  data-testid="about-page-photo"
                  className="w-full h-auto block"
                />
                <div
                  className="absolute inset-0 mix-blend-multiply opacity-25 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(225,6,0,0.55) 0%, transparent 50%, rgba(30,111,224,0.55) 100%)",
                  }}
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black/85 backdrop-blur-sm border-t border-[#262626] p-4">
                  <p className="text-[10px] tracking-[0.25em] uppercase font-mono text-[#E10600]">
                    Hosted By
                  </p>
                  <p className="font-display text-2xl uppercase leading-none text-white mt-1">
                    Manuel Santana
                  </p>
                  <p className="mt-1 text-xs text-zinc-400">
                    NYC · Boston · Mon 7PM EST
                  </p>
                </div>
              </div>
            </div>

            {/* Topics list */}
            <div className="mt-8">
              <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500 font-mono mb-3">
                What We Cover
              </p>
              <div className="flex flex-wrap gap-2">
                {TOPICS_LONG.map((t, i) => (
                  <span
                    key={t}
                    className={`text-[10px] tracking-[0.2em] uppercase font-bold px-2.5 py-1.5 border ${
                      i % 2 === 0
                        ? "border-[#E10600]/40 text-[#E10600]"
                        : "border-[#1E6FE0]/40 text-[#1E6FE0]"
                    }`}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </aside>
        </section>

        {/* Pillars grid */}
        <section className="mb-24">
          <div className="mb-10">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#1E6FE0] mb-3">
              What Sets It Apart
            </p>
            <h2 className="font-display text-5xl md:text-6xl uppercase leading-none">
              The <span className="text-[#E10600]">Approach</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#262626] border border-[#262626]">
            {PILLARS.map((p, i) => (
              <article
                key={p.title}
                data-testid={`about-pillar-${i}`}
                className="bg-[#0A0A0A] hover:bg-[#121212] p-8 md:p-10 transition-colors group"
              >
                <div className="flex items-start gap-5">
                  <span
                    className="w-12 h-12 flex items-center justify-center flex-shrink-0 border"
                    style={{ borderColor: p.color }}
                  >
                    <p.icon className="w-5 h-5" style={{ color: p.color }} />
                  </span>
                  <div>
                    <p
                      className="text-[10px] tracking-[0.25em] uppercase font-mono"
                      style={{ color: p.color }}
                    >
                      Pillar · {String(i + 1).padStart(2, "0")}
                    </p>
                    <h3 className="mt-2 font-display text-2xl md:text-3xl uppercase leading-tight text-white">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-zinc-400 leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Host Bio */}
        <section
          data-testid="host-bio-section"
          className="mb-24 border-t border-[#262626] pt-16"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#1E6FE0] mb-3">
                Host Bio
              </p>
              <h2 className="font-display text-5xl md:text-6xl uppercase leading-none">
                About <span className="text-[#E10600]">Manuel</span>
              </h2>
              <p className="mt-6 text-zinc-400 text-sm">
                Press-ready bio. Free to reproduce with attribution.
              </p>

              <div className="mt-8 grid grid-cols-2 gap-px bg-[#262626] border border-[#262626]">
                <BioStat label="Nationality" value="American" />
                <BioStat label="Based" value="New York City" />
                <BioStat label="Filmed In" value="NYC · Boston" />
                <BioStat label="Schedule" value="Mon · 7PM EST" />
                <BioStat label="Host IG" value="@iammanuelsantana" />
                <BioStat label="Show IG" value="@theproveyourpointpodcast" />
              </div>

              <div className="mt-8 flex flex-wrap gap-2">
                {[
                  "Media Personality",
                  "Podcast Host",
                  "Commentator",
                  "Relationship Educator",
                ].map((r, i) => (
                  <span
                    key={r}
                    className={`text-[10px] tracking-[0.2em] uppercase font-bold px-2.5 py-1.5 border ${
                      i % 2 === 0
                        ? "border-[#E10600]/40 text-[#E10600]"
                        : "border-[#1E6FE0]/40 text-[#1E6FE0]"
                    }`}
                  >
                    {r}
                  </span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7 space-y-6 text-zinc-300 text-base md:text-lg leading-relaxed">
              <p>
                <span className="text-white font-bold">Manuel Santana</span> is an
                American media personality, podcast host, commentator, and relationship
                educator best known as the creator and host of{" "}
                <span className="text-white font-bold">The Prove Your Point Podcast</span>.
              </p>
              <p>
                Through his podcast, interviews, and digital media appearances, Santana
                explores topics including{" "}
                <span className="text-white">relationships</span>,{" "}
                <span className="text-white">dating dynamics</span>,{" "}
                <span className="text-white">gender relations</span>,{" "}
                <span className="text-white">personal development</span>, and{" "}
                <span className="text-white">modern culture</span>. Known for his direct
                communication style and willingness to discuss controversial subjects,
                he has built a platform focused on encouraging open dialogue between
                people with different perspectives.
              </p>
              <p>
                As host of The Prove Your Point Podcast, Santana's mission is to help
                foster greater understanding between men and women by promoting{" "}
                <span className="text-[#E10600] font-bold">accountability</span>,{" "}
                <span className="text-[#1E6FE0] font-bold">self-awareness</span>, and{" "}
                <span className="text-white font-bold">meaningful conversation</span>.
              </p>

              <div className="mt-4 pt-6 border-t border-[#262626] flex flex-wrap gap-3">
                <button
                  data-testid="bio-copy-btn"
                  onClick={() => {
                    const bio = `Manuel Santana is an American media personality, podcast host, commentator, and relationship educator best known as the creator and host of The Prove Your Point Podcast.

Through his podcast, interviews, and digital media appearances, Santana explores topics including relationships, dating dynamics, gender relations, personal development, and modern culture. Known for his direct communication style and willingness to discuss controversial subjects, he has built a platform focused on encouraging open dialogue between people with different perspectives.

As host of The Prove Your Point Podcast, Santana's mission is to help foster greater understanding between men and women by promoting accountability, self-awareness, and meaningful conversation.`;
                    navigator.clipboard.writeText(bio);
                  }}
                  className="btn-secondary"
                >
                  Copy Bio Text
                </button>
                <Link to="/media" data-testid="bio-press-kit" className="btn-primary">
                  Press Kit & Photos
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Host strip + CTAs */}
        <section className="border-t border-[#262626] pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E10600] mb-3">
                The Host
              </p>
              <h2 className="font-display text-5xl md:text-6xl uppercase leading-none">
                Manuel <span className="text-[#1E6FE0]">Santana</span>
              </h2>
              <p className="mt-6 text-zinc-300 leading-relaxed max-w-xl">
                Media personality, commentator, and the unfiltered voice behind Prove
                Your Point. Manuel hosts long-form conversations from{" "}
                <span className="text-white">New York City</span> and{" "}
                <span className="text-white">Boston</span> — live Mondays from 7PM to
                9PM EST.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/be-a-guest" data-testid="about-cta-guest" className="btn-primary">
                  Apply To Be A Guest
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/media" data-testid="about-cta-media" className="btn-blue">
                  Press & Media Kit
                </Link>
                <a
                  href={SOCIALS.host_instagram}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="about-cta-ig"
                  className="btn-secondary"
                >
                  Follow @iammanuelsantana
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative bg-[#0A0A0A] border border-[#262626] overflow-hidden">
                <img
                  src={HOST_PHOTO_1}
                  alt="Manuel Santana"
                  data-testid="about-page-host-photo"
                  className="w-full h-auto block"
                />
                <div
                  className="absolute inset-0 mix-blend-multiply opacity-20 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(225,6,0,0.6) 0%, transparent 50%, rgba(30,111,224,0.6) 100%)",
                  }}
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function BioStat({ label, value }) {
  return (
    <div className="bg-[#0A0A0A] p-4">
      <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-mono">
        {label}
      </p>
      <p className="mt-1 text-sm text-white">{value}</p>
    </div>
  );
}
